import React from 'react';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';
import { Lightbulb, MapPin, Users, Building2, Shield, Heart, ArrowRight, CheckCircle2, HelpCircle, Trophy, Compass, Flag, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';

export default function Association() {
  const d = useSection('association');
  const h = d.hero || {};
  const items = [d.origin, d.italy, d.concrete, d.name, d.independent, d.operation, d.network, d.circles];

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-br from-emerald-50 via-white to-sky-50 relative overflow-hidden">
        <div className="absolute top-32 -left-32 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-32 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {h.eyebrow && <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-emerald-100 shadow-sm text-xs font-bold uppercase tracking-wider text-emerald-700 mb-5"><Flag className="w-3.5 h-3.5" />{h.eyebrow}</span>}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-4 leading-tight">{h.title} <span className="gradient-text">{h.titleHighlight}</span></h1>
            {h.suffix && <p className="text-xl lg:text-2xl text-slate-700 font-medium mb-6">{h.suffix}</p>}
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto italic">« {h.description} »</p>
          </Reveal>
        </div>
      </section>

      {/* CONTEXT */}
      {d.context && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-5">
              {(d.context.paragraphs || []).map((p, i) => (
                <Reveal key={i} delay={i * 60}>
                  <p className={`text-slate-700 leading-relaxed ${i === 2 ? 'text-lg font-display font-bold text-sky-950 pl-5 border-l-4 border-emerald-500' : ''} ${i === 3 ? 'text-lg text-sky-800 font-semibold' : ''}`}>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ORIGIN */}
      {d.origin && (
        <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"><Lightbulb className="w-6 h-6 text-white" /></div>
                <h2 className="font-display font-extrabold text-3xl text-sky-950">{d.origin.title}</h2>
              </div>
              <div className="space-y-4 mb-6">
                {(d.origin.paragraphs || []).map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
              </div>
              <div className="p-6 rounded-3xl bg-white border border-sky-100 mb-4">
                <p className="text-sm text-slate-500 mb-3 font-semibold uppercase tracking-wider flex items-center gap-2"><HelpCircle className="w-4 h-4" />Ce quotidien devient une source d'interrogation puis d'indignation :</p>
                <ul className="space-y-2">
                  {(d.origin.questions || []).map((q, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700"><span className="text-emerald-600 font-bold flex-shrink-0">•</span><span>{q}</span></li>
                  ))}
                </ul>
              </div>
              <p className="text-lg font-display font-bold text-sky-950 text-center">{d.origin.conclusion}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* ITALY */}
      {d.italy && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-red-500 flex items-center justify-center"><MapPin className="w-6 h-6 text-white" /></div>
                <h2 className="font-display font-extrabold text-3xl text-sky-950">{d.italy.title}</h2>
              </div>
              <div className="space-y-4 mb-6">
                {(d.italy.paragraphs || []).map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
              </div>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 mb-4">
                <p className="text-sm font-bold text-sky-900 uppercase tracking-wider mb-3">Le modèle est déjà massif :</p>
                <ul className="space-y-2">
                  {(d.italy.stats || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{s}</span></li>
                  ))}
                </ul>
              </div>
              <p className="italic text-slate-600">{d.italy.conclusion}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* CONCRETE */}
      {d.concrete && (
        <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display font-extrabold text-3xl text-sky-950 mb-6 text-center">{d.concrete.title}</h2>
              <div className="space-y-4 max-w-3xl mx-auto mb-6">
                {(d.concrete.paragraphs || []).map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
              </div>
              <div className="p-6 rounded-3xl bg-white border border-sky-100 max-w-3xl mx-auto mb-6">
                <p className="text-sm font-bold text-sky-900 uppercase tracking-wider mb-3">Les débuts reposent sur des soutiens très concrets :</p>
                <ul className="space-y-2">
                  {(d.concrete.support || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{s}</span></li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-3xl bg-gradient-to-r from-emerald-500 to-sky-500 text-white text-center shadow-xl max-w-3xl mx-auto mb-8">
                <Trophy className="w-8 h-8 mx-auto mb-3" />
                <p className="font-display font-bold text-xl">{d.concrete.milestone}</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <div className="aspect-[16/10] rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-sky-100 to-emerald-100 border-4 border-white flex items-center justify-center">
                  {d.concrete.photo ? <img src={resolveImage(d.concrete.photo)} alt="" className="w-full h-full object-cover" /> : (
                    <div className="text-center p-8"><Users className="w-16 h-16 text-sky-500 mx-auto mb-3" /><p className="text-sky-700 font-semibold">Photo inauguration</p><p className="text-xs text-slate-500 mt-1">À téléverser depuis le CMS</p></div>
                  )}
                </div>
                {d.concrete.photoCaption && <p className="text-xs text-slate-500 mt-3 text-center italic">{d.concrete.photoCaption}</p>}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* NAME */}
      {d.name && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center"><HelpCircle className="w-6 h-6 text-white" /></div>
                <h2 className="font-display font-extrabold text-3xl text-sky-950">{d.name.title}</h2>
              </div>
              <div className="space-y-4 mb-6">
                {(d.name.paragraphs || []).map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {(d.name.evocations || []).map((e, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-slate-100 text-center">
                    <p className="text-sm text-slate-600 italic">{e}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">{d.name.reality}</p>
              <p className="text-slate-700 leading-relaxed mb-4">{d.name.adoption}</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold">
                <Shield className="w-4 h-4" />{d.name.trademark}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* INDEPENDENT & OPERATION */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {d.independent && (
            <Reveal>
              <div className="p-8 rounded-3xl bg-white border border-sky-100 h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center mb-5"><Shield className="w-6 h-6 text-white" /></div>
                <h3 className="font-display font-extrabold text-2xl text-sky-950 mb-4">{d.independent.title}</h3>
                <div className="space-y-3 mb-4">
                  {(d.independent.paragraphs || []).map((p, i) => <p key={i} className="text-sm text-slate-700 leading-relaxed">{p}</p>)}
                </div>
                <p className="text-sm font-display font-bold text-emerald-700 border-l-4 border-emerald-500 pl-3">{d.independent.objective}</p>
                {d.independent.pdfLink && (
                  <a
                    href={resolveImage(d.independent.pdfLink)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all group"
                    data-testid="association-pdf-download"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Consulter la déclaration officielle</span>
                    <Download className="w-3.5 h-3.5 opacity-70 group-hover:translate-y-0.5 transition-transform" />
                  </a>
                )}
              </div>
            </Reveal>
          )}
          {d.operation && (
            <Reveal delay={100}>
              <div className="p-8 rounded-3xl bg-white border border-sky-100 h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-5"><Building2 className="w-6 h-6 text-white" /></div>
                <h3 className="font-display font-extrabold text-2xl text-sky-950 mb-4">{d.operation.title}</h3>
                <p className="text-sm text-slate-600 mb-3 font-semibold">{d.operation.intro}</p>
                <ul className="space-y-2 mb-4">
                  {(d.operation.items || []).map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{it}</span></li>
                  ))}
                </ul>
                <p className="text-sm italic text-slate-600">{d.operation.conclusion}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* NETWORK */}
      {d.network && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <Compass className="w-12 h-12 text-sky-600 mx-auto mb-5" />
              <h2 className="font-display font-extrabold text-3xl text-sky-950 mb-5">{d.network.title}</h2>
              <div className="space-y-4 mb-6">
                {(d.network.paragraphs || []).map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
              </div>
              <div className="p-6 rounded-3xl bg-gradient-to-r from-sky-500 to-emerald-500 text-white">
                <p className="font-display font-bold text-lg">{d.network.principle}</p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* CIRCLES */}
      {d.circles && (
        <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-5"><Users className="w-7 h-7 text-white" /></div>
                  <h2 className="font-display font-extrabold text-3xl text-sky-950 mb-4">{d.circles.title}</h2>
                  <p className="text-slate-700 leading-relaxed">{d.circles.description}</p>
                  {d.circles.pdfUrl && (
                    <a
                      href={d.circles.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      data-testid="association-circles-pdf-btn"
                      className="group inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white text-sm font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      {d.circles.pdfLabel || 'En savoir plus'}
                    </a>
                  )}
                </div>
                <div className="aspect-square rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-amber-100 to-orange-100 border-4 border-white flex items-center justify-center">
                  {d.circles.photo ? <img src={resolveImage(d.circles.photo)} alt="" className="w-full h-full object-cover" /> : (
                    <div className="text-center p-6"><Users className="w-16 h-16 text-amber-600 mx-auto mb-3" /><p className="text-amber-700 font-semibold text-sm">Photo Cercle de Citoyens</p></div>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-950 to-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-14 h-14 text-emerald-300 mx-auto mb-6 fill-emerald-300" />
          <h2 className="font-display font-extrabold text-4xl lg:text-5xl mb-5">{d.finalCta?.title || "Rejoignez l'initiative"}</h2>
          <p className="text-lg text-sky-200 mb-8 max-w-2xl mx-auto">{d.finalCta?.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/soutenir"><Button size="lg" className="bg-white text-sky-900 hover:bg-sky-50 rounded-xl px-7 font-semibold">Faire un don<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            <Link to="/je-soutiens"><Button size="lg" variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-xl px-7" data-testid="assoc-cta-volunteer">Devenir bénévole</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
